// Э-6. Загрузка бригад по дням: где специальность работает, где простаивает, где идёт на двух этажах сразу;
// бригады и бригадиры объекта и журнал «кто когда работал» (встреча 24.10.2026).

import { useState } from "react";
import { addDays, fmtDate, fmtDay } from "../../shared/format";
import { useLive } from "../../shared/live";
import { TopBar } from "../../shared/shell";
import { setQuery, useRoute } from "../../shared/router";
import { Button, Loading, Tabs, useAsync } from "../../shared/ui";
import { api, can, floorAbbr, floorName, live, useCtx } from "../ctx";
import { BrigadesTab, JournalTab } from "./Brigades";

type Tab = "load" | "brigades" | "journal";

export function CrewsPage({ id }: { id: string }) {
  const { me } = useCtx();
  const r = useRoute();
  const tab = (r.query.get("tab") as Tab) || "load";
  const b = useAsync<any>(() => api.get(`/buildings/${id}`), [id]);
  return (
    <>
      <TopBar title="Бригады" />
      <div className="content">
        <Tabs<Tab> value={tab} onChange={(v) => setQuery({ tab: v === "load" ? null : v })} items={[
          { value: "load", label: "Загрузка по специальностям" }, { value: "brigades", label: "Бригады и бригадиры" }, { value: "journal", label: "Кто когда работал" },
        ]} />
        {tab === "load" && <LoadTab id={id} />}
        {tab !== "load" && !b.data && <Loading error={b.error} onRetry={b.reload} />}
        {tab === "brigades" && b.data && <BrigadesTab projectId={b.data.project_id} canEdit={can(me, "admin.projects") || can(me, "issues.manage")} />}
        {tab === "journal" && b.data && <JournalTab projectId={b.data.project_id} />}
      </div>
    </>
  );
}

function LoadTab({ id }: { id: string }) {
  const [from, setFrom] = useState<string | null>(null);
  const d = useAsync<any>(() => api.get(`/buildings/${id}/crew-load`, from ? { from, to: addDays(from, 21) } : undefined), [id, from]);
  const [sel, setSel] = useState<{ crew: any; cell: any } | null>(null);
  useLive(live, ["report.", "cycle."], () => d.reload(), 1500);
  const v = d.data;
  return (
    <>
      {v && <div className="row tight">
        <Button onClick={() => setFrom(addDays(v.start, -14))}>Раньше</Button>
        <Button onClick={() => setFrom(null)}>Сегодня</Button>
        <Button onClick={() => setFrom(addDays(v.start, 14))}>Позже</Button>
      </div>}
      <div className="stack">
        {!v ? <Loading error={d.error} onRetry={d.reload} /> : (
          <div className="panel">
            <div className="pb gantt">
              <table>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "0 10px", minWidth: 150, position: "sticky", left: 0, background: "var(--panel)", zIndex: 2 }}>Специальность</th>
                    {v.rows[0]?.cells.map((c: any) => (
                      <th key={c.date} className={`day wide${c.date === v.today ? " today" : ""}`} title={fmtDate(c.date)}>
                        {Number(c.date.slice(8))}<small>{fmtDay(c.date).split(",")[0]}</small>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {v.rows.map((r: any) => (
                    <tr key={r.code}>
                      <td className="name"><b>{r.code}</b> · {r.name}<div className="muted small">в смену {r.planned_headcount} чел.</div></td>
                      {r.cells.map((c: any) => {
                        const future = c.date > v.today;
                        const tone = !c.workday ? "" : c.tasks.length === 0 ? (c.fact ? "idle" : "") : c.parallel ? "par" : "busy";
                        const floors = [...new Set(c.tasks.map((t: any) => t.floor))] as number[];
                        const grips = ([...new Set(c.tasks.map((t: any) => t.grip))] as string[]).sort((a, b) => (a[0] === b[0] ? a.localeCompare(b) : a[0] === "С" ? -1 : 1));
                        const plan = c.planned_per_day || 0;
                        const short = c.date < v.today && c.fact != null && plan > 0 && c.fact < plan * (v.headcount_ratio ?? 0.8);
                        return (
                          <td key={c.date} className={`d${c.workday ? "" : " off"}${c.date === v.today ? " today" : ""}`}>
                            <button className={`load cl ${tone}${future ? " fut" : ""}`} onClick={() => setSel({ crew: r, cell: c })}
                              title={`${fmtDate(c.date)}: ${c.tasks.map((t: any) => `${floorAbbr(t.floor)} ${t.grip} ${t.work}`).join(", ") || "работ по плану нет"}`}>
                              <span className="g">{grips.join(" ")}{floors.length > 1 ? <small> эт. {Math.min(...floors)}–{Math.max(...floors)}</small> : floors.length === 1 ? <small> эт. {floors[0]}</small> : null}</span>
                              {(plan > 0 || c.fact != null) && <span className={`n${short ? " short" : ""}`}>{plan || "—"}{!future ? ` / ${c.fact ?? "—"}` : ""}</span>}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pb row between small" style={{ flexWrap: "wrap", gap: 8 }}>
              <div className="legend fills">
                <span><i className="sw-planned" />работает на своих захватках</span>
                <span><i className="sw-transit" />на двух этажах сразу</span>
                <span><i className="sw-loading" />люди есть, работ по плану нет</span>
                <span><b style={{ color: "var(--st-conflict)" }}>9 / 5</b> — людей меньше {Math.round((v.headcount_ratio ?? 0.8) * 100)}% плана</span>
              </div>
              <span className="muted">В ячейке: захватки по плану и люди за сутки — план / факт по отчётам{v.shifts > 1 ? ` (${v.shifts} смены)` : ""}</span>
            </div>
          </div>
        )}
        {sel && (
          <div className="panel pad stack tight">
            <div className="row between"><h3>{sel.crew.name}, {fmtDate(sel.cell.date)}</h3><Button size="small" variant="ghost" onClick={() => setSel(null)}>Закрыть</Button></div>
            {sel.cell.tasks.length === 0 ? <p className="muted">Работ по плану нет.</p> : (
              <ul style={{ margin: 0, paddingLeft: 18 }}>{sel.cell.tasks.map((t: any) => <li key={`${t.floor}${t.key}`}>{floorName(t.floor, false)} · {t.grip} · {t.work}</li>)}</ul>
            )}
            <div className="small">Людей: по плану {sel.cell.planned || "—"}{sel.cell.date <= v.today ? `, по отчётам ${sel.cell.fact ?? "—"} (человеко-смен)` : ""}</div>
          </div>
        )}
      </div>
    </>
  );
}
